import React, { useState, useEffect } from 'react';
import { usePortfolioContext } from './PortfolioContext';
import apiService from '../../services/ApiService';
import AssetManager from './AssetManager';
import TagManager from './TagManager';
import SharingManager from './SharingManager';
import OverviewTab from './tabs/OverviewTab';
import FundingTab from './tabs/FundingTab';
import DistributionsTab from './tabs/DistributionsTab';
import ConcentrationTab from './tabs/ConcentrationTab';
import { cn } from '../../lib/utils';

const PORTFOLIO_TABS = [
    { id: 'assets', label: 'Assets', icon: 'business' },
    { id: 'tags', label: 'Tags', icon: 'sell' },
    { id: 'sharing', label: 'Sharing', icon: 'group' },
    { id: 'overview', label: 'Overview', icon: 'dashboard' },
    { id: 'funding', label: 'Funding', icon: 'account_balance' },
    { id: 'distributions', label: 'Distributions', icon: 'payments' },
    { id: 'concentration', label: 'Concentration', icon: 'pie_chart' },
];

export default function PortfolioTabs() {
    const { selectedGroup } = usePortfolioContext();
    const [activeTab, setActiveTab] = useState('assets');
    const [viewData, setViewData] = useState<any>(null);
    const [loadingView, setLoadingView] = useState(false);

    useEffect(() => {
        if (!selectedGroup || ['assets', 'tags', 'sharing'].includes(activeTab)) {
            setViewData(null);
            return;
        }

        const fetchViewData = async () => {
            setLoadingView(true);
            try {
                let response;
                switch (activeTab) {
                    case 'overview':
                        response = await apiService.getPortfolioOverview(selectedGroup.id);
                        break;
                    case 'funding':
                        response = await apiService.getPortfolioFunding(selectedGroup.id);
                        break;
                    case 'distributions':
                        response = await apiService.getPortfolioDistributions(selectedGroup.id);
                        break;
                    case 'concentration':
                        response = await apiService.getPortfolioConcentration(selectedGroup.id);
                        break;
                }
                if (response && response.ok) {
                    const data = await response.json();
                    setViewData(data);
                } else {
                    setViewData(null);
                }
            } catch {
                setViewData(null);
            } finally {
                setLoadingView(false);
            }
        };

        fetchViewData();
    }, [selectedGroup?.id, selectedGroup?.computed_at, activeTab]);

    return (
        <div>
            {/* Tab bar */}
            <div className="flex gap-1 mb-6 bg-white rounded-lg border border-slate-200 p-1">
                {PORTFOLIO_TABS.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={cn(
                            'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
                            activeTab === tab.id
                                ? 'bg-blue-600 text-white'
                                : 'text-slate-600 hover:bg-slate-100'
                        )}
                    >
                        <span className="material-symbols-outlined text-[18px]">{tab.icon}</span>
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab content */}
            {activeTab === 'assets' && <AssetManager />}
            {activeTab === 'tags' && <TagManager />}
            {activeTab === 'sharing' && <SharingManager />}

            {!['assets', 'tags', 'sharing'].includes(activeTab) && loadingView && (
                <div className="flex items-center justify-center py-16">
                    <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-slate-500">Loading view data...</span>
                    </div>
                </div>
            )}

            {!['assets', 'tags', 'sharing'].includes(activeTab) && !loadingView && !viewData && (
                <div className="text-center py-16 text-slate-400">
                    <span className="material-symbols-outlined text-[48px] block mb-3">calculate</span>
                    <p className="text-base font-medium">No compute results yet</p>
                    <p className="text-sm mt-1">Click "Compute" to generate portfolio analytics</p>
                </div>
            )}

            {activeTab === 'overview' && viewData && <OverviewTab data={viewData} />}
            {activeTab === 'funding' && viewData && <FundingTab data={viewData} />}
            {activeTab === 'distributions' && viewData && <DistributionsTab data={viewData} />}
            {activeTab === 'concentration' && viewData && <ConcentrationTab data={viewData} />}
        </div>
    );
}
